import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import type { PayrollPreviewResult, PayslipLineItemInput } from '@/services/compensation/types';
import type { ExportContentOptions } from './exportConfig';
import { getCsvColumns } from './exportConfig';

interface PreviewPdfOptions {
  currencyCode: string;
  contentOptions?: ExportContentOptions;
}

function fmtCurrency(amount: number, currencyCode: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);
}

function addDraftWatermark(doc: jsPDF) {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    doc.saveGraphicsState();
    doc.setFontSize(60);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(200, 200, 200);
    doc.setGState(new (doc as any).GState({ opacity: 0.3 }));

    const cx = pageW / 2;
    const cy = pageH / 2;
    doc.text('DRAFT PREVIEW', cx, cy, {
      align: 'center',
      angle: 45,
    });
    doc.restoreGraphicsState();
  }
}

/**
 * Generate a PDF payslip from in-memory preview data (not yet saved to DB).
 * Matches the layout of generatePayslipPdf() but adds a DRAFT watermark.
 * Content sections are controlled by ExportContentOptions.
 */
export async function generatePreviewPayslipPdf(
  data: PayrollPreviewResult,
  payPeriodStart: Date,
  payPeriodEnd: Date,
  options: PreviewPdfOptions
): Promise<Blob> {
  const { currencyCode, contentOptions } = options;
  const opts: ExportContentOptions = contentOptions ?? {
    includeLineItems: true,
    includeYTD: true,
    includeHoursBreakdown: true,
  };
  const fmt = (n: number) => fmtCurrency(n, currencyCode);
  const calc = data.calculation;
  const summary = calc.calculationSummary;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = margin;

  // ── Header ──
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Payslip Preview', margin, y + 7);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(`Generated ${format(new Date(), 'MMM dd, yyyy HH:mm')}`, pageWidth - margin, y + 7, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  y += 14;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  // ── Employee & Pay Period ──
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Employee', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(data.employeeName, margin + 25, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Pay Period', pageWidth / 2, y);
  doc.setFont('helvetica', 'normal');
  doc.text(`${format(payPeriodStart, 'MMM dd')} – ${format(payPeriodEnd, 'MMM dd, yyyy')}`, pageWidth / 2 + 28, y);

  y += 12;

  // ── Summary Box ──
  const boxY = y;
  const boxH = 28;
  doc.setFillColor(248, 249, 250);
  doc.roundedRect(margin, boxY, pageWidth - margin * 2, boxH, 2, 2, 'F');

  const summaryItems = [
    { label: 'Gross Pay', value: fmt(calc.totalGrossPay), color: [22, 163, 74] as [number, number, number] },
    { label: 'Deductions', value: fmt(calc.totalDeductions), color: [220, 38, 38] as [number, number, number] },
    { label: 'Net Pay', value: fmt(calc.netPay), color: [37, 99, 235] as [number, number, number] },
    { label: 'Total Hours', value: (summary.regularHours + summary.overtimeHours + summary.holidayHours).toFixed(1), color: [0, 0, 0] as [number, number, number] },
  ];

  const colW = (pageWidth - margin * 2) / summaryItems.length;
  summaryItems.forEach((item, i) => {
    const x = margin + colW * i + colW / 2;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(item.label, x, boxY + 10, { align: 'center' });

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...item.color);
    doc.text(item.value, x, boxY + 20, { align: 'center' });
  });

  doc.setTextColor(0, 0, 0);
  y = boxY + boxH + 10;

  // ── Line Items Table (conditional) ──
  if (opts.includeLineItems) {
    const items = calc.lineItems;
    if (items && items.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Earnings & Deductions', margin, y);
      y += 6;

      const earnings = items.filter((i: PayslipLineItemInput) => i.category === 'earnings');
      const deductions = items.filter((i: PayslipLineItemInput) => ['deductions', 'taxes'].includes(i.category || ''));

      const tableRows: (string | number)[][] = [];

      if (earnings.length > 0) {
        tableRows.push([{ content: 'EARNINGS', colSpan: 3, styles: { fillColor: [232, 245, 233], fontStyle: 'bold', fontSize: 8 } } as any]);
        earnings.forEach(item => {
          tableRows.push([
            item.description || 'Earnings',
            item.category || '',
            fmt(Number(item.gross_amount || 0)),
          ]);
        });
      }

      if (deductions.length > 0) {
        tableRows.push([{ content: 'DEDUCTIONS & TAXES', colSpan: 3, styles: { fillColor: [254, 226, 226], fontStyle: 'bold', fontSize: 8 } } as any]);
        deductions.forEach(item => {
          tableRows.push([
            item.description || 'Deduction',
            item.category || '',
            fmt(Number(item.gross_amount || 0)),
          ]);
        });
      }

      autoTable(doc, {
        startY: y,
        head: [['Description', 'Category', 'Amount']],
        body: tableRows,
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [55, 65, 81], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 35 },
          2: { cellWidth: 35, halign: 'right' },
        },
        theme: 'grid',
      });

      y = (doc as any).lastAutoTable.finalY + 10;
    } else {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(120, 120, 120);
      doc.text('No line items calculated for this preview.', margin, y);
      doc.setTextColor(0, 0, 0);
      y += 10;
    }
  }

  // ── Year-to-Date Summary (conditional) ──
  if (opts.includeYTD && data.yearToDateSummary && data.yearToDateSummary.periods_count > 0) {
    if (y > doc.internal.pageSize.getHeight() - 50) {
      doc.addPage();
      y = margin;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Year-to-Date Summary', margin, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['YTD Gross Earnings', fmt(data.yearToDateSummary.total_gross)],
        ['YTD Net Pay', fmt(data.yearToDateSummary.total_net)],
        ['YTD Total Hours', data.yearToDateSummary.total_hours.toFixed(1)],
        ['Pay Periods', String(data.yearToDateSummary.periods_count)],
      ],
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [55, 65, 81], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right' } },
      theme: 'grid',
    });
  }

  // ── Footer ──
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(160, 160, 160);
    const pageH = doc.internal.pageSize.getHeight();
    doc.text('DRAFT — This is a preview and has not been finalized.', margin, pageH - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageH - 8, { align: 'right' });
  }

  // ── Watermark ──
  addDraftWatermark(doc);

  return doc.output('blob');
}

/**
 * Generate CSV from in-memory preview payroll data.
 * Columns are driven by ExportContentOptions so the output
 * always matches what the dialog preview promises.
 */
export function generatePreviewCSV(
  data: PayrollPreviewResult[],
  payPeriodStart: Date,
  payPeriodEnd: Date,
  currencyCode: string,
  contentOptions?: ExportContentOptions,
): { content: string; filename: string } {
  const opts: ExportContentOptions = contentOptions ?? {
    includeLineItems: true,
    includeYTD: false,
    includeHoursBreakdown: true,
  };

  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const headers = getCsvColumns(opts);

  const rows = data.map(r => {
    const s = r.calculation.calculationSummary;
    const cells: string[] = [
      escape(r.employeeName),
      escape(r.profileId.slice(-8)),
    ];

    if (opts.includeHoursBreakdown) {
      cells.push(
        s.regularHours.toFixed(2),
        s.overtimeHours.toFixed(2),
        s.holidayHours.toFixed(2),
        s.paidLeaveHours.toFixed(2),
      );
    }

    cells.push(
      r.calculation.totalGrossPay.toFixed(2),
      r.calculation.totalDeductions.toFixed(2),
      r.calculation.netPay.toFixed(2),
    );

    if (opts.includeYTD) {
      const ytd = r.yearToDateSummary;
      cells.push(
        (ytd?.total_gross ?? 0).toFixed(2),
        (ytd?.total_net ?? 0).toFixed(2),
        (ytd?.total_hours ?? 0).toFixed(2),
        String(ytd?.periods_count ?? 0),
      );
    }

    return cells.join(',');
  });

  const content = [headers.join(','), ...rows].join('\n');
  const filename = `payroll_preview_${format(payPeriodStart, 'yyyy-MM-dd')}_to_${format(payPeriodEnd, 'yyyy-MM-dd')}.csv`;

  return { content, filename };
}

/**
 * Trigger a browser download from a Blob.
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Trigger a browser download from a string content.
 */
export function downloadString(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  downloadBlob(blob, filename);
}
